import moment from 'moment-timezone'
import { CellularPayPeriod, CellularSavedCard } from './types'

// Billing dates come from Stripe as UTC; format them in UTC so a period never shifts a day.
const parse = (value: string) => moment.utc(value)

// The API tells us the currency of the invoice; USD is only the fallback.
const FALLBACK_CURRENCY = 'USD'

const format = (amount: number, currency?: string | null, digits?: number): string => {
  const code = (currency || FALLBACK_CURRENCY).toUpperCase()
  const fixed = digits === undefined ? 2 : digits
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: fixed,
      maximumFractionDigits: fixed
    }).format(amount)
  } catch (error) {
    // Intl throws on a currency code it does not know; still show the amount.
    return `${amount.toFixed(fixed)} ${code}`
  }
}

export const formatMoney = (amount?: number | null, currency?: string | null): string =>
  format(Number.isFinite(Number(amount)) ? Number(amount) : 0, currency)

// Whole amounts read better without the cents ("Billed at $240 per year").
export const formatWholeMoney = (amount: number, currency?: string | null): string =>
  Number.isInteger(amount) ? format(amount, currency, 0) : formatMoney(amount, currency)

export const formatDate = (value?: string | null): string => {
  if (!value) return ''
  const date = parse(value)
  return date.isValid() ? date.format('MMMM D, YYYY') : ''
}

// Whole days from today until a billing date, negative once it has gone by; null
// when there is no usable date. In UTC, like every other billing date here.
export const daysUntil = (value?: string | null, now: number = Date.now()): number | null => {
  if (!value) return null
  const date = parse(value)
  if (!date.isValid()) return null
  return date.startOf('day').diff(moment.utc(now).startOf('day'), 'days')
}

export const formatMonthYear = (value: string): string => {
  const date = parse(value)
  return date.isValid() ? date.format('MMM YYYY') : ''
}

export const formatPeriod = ({ start, end }: CellularPayPeriod): string =>
  `${formatMonthYear(start)} - ${formatMonthYear(end)}`

const twoDigits = (value: number): string => `0${value}`.slice(-2)

export const formatCardExpiry = ({ expMonth, expYear }: CellularSavedCard): string =>
  `${twoDigits(expMonth)}/${twoDigits(expYear % 100)}`

// Stripe reports brands in lower case ("visa", "amex"); show them the way the card says it.
const BRAND_NAMES: Record<string, string> = {
  amex: 'American Express',
  diners: 'Diners Club',
  discover: 'Discover',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa'
}

export const formatCardBrand = (brand: string): string => {
  const known = BRAND_NAMES[(brand || '').toLowerCase()]
  if (known) return known
  return brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card'
}

export const formatSavedCard = (card: CellularSavedCard): string =>
  `${formatCardBrand(card.brand)} ending in ${card.last4}`

export const yearsLabel = (count: number): string => `${count} year${count === 1 ? '' : 's'}`

export const daysLabel = (count: number): string => `${count} day${count === 1 ? '' : 's'}`

export const controllersLabel = (count: number): string =>
  `${count} controller${count === 1 ? '' : 's'}`

// One billed year, give or take month lengths and leap days.
const WHOLE_YEAR_MIN_DAYS = 360
const WHOLE_YEAR_MAX_DAYS = 370

const isWholeYear = ({ start, end }: CellularPayPeriod): boolean => {
  const from = parse(start)
  const to = parse(end)
  if (!from.isValid() || !to.isValid()) return false
  const days = to.diff(from, 'days')
  return days >= WHOLE_YEAR_MIN_DAYS && days <= WHOLE_YEAR_MAX_DAYS
}

export const areWholeYears = (periods: CellularPayPeriod[]): boolean =>
  periods.length > 0 && periods.every(isWholeYear)

// The rate under the total ("Billed at $240 per year") is derived, so it can
// contradict the bill: claim it only when every period is a year and they all cost
// the same. Otherwise the periods speak for themselves.
export const yearlyRate = (periods: CellularPayPeriod[]): number | null => {
  if (!areWholeYears(periods)) return null
  const [first, ...rest] = periods
  return rest.every(period => period.amountUsd === first.amountUsd) ? first.amountUsd : null
}

// What the money buys, in the bill's own terms; empty when there are no periods.
export const periodsLabel = (periods: CellularPayPeriod[]): string => {
  if (periods.length === 0) return ''
  return areWholeYears(periods)
    ? `for ${yearsLabel(periods.length)} of service`
    : 'for the periods listed below'
}
